<template>
  <v-navigation-drawer v-model="drawer">
    <v-tooltip text="Human Resources Information System" location="right">
      <template v-slot:activator="{ props }">
        <v-list-item v-bind="props" lines="two" :prepend-avatar="require('@/assets/workforce_logo.png')"
          title="WorkForce" title-class="bold-title"></v-list-item>
      </template>
    </v-tooltip>

    <v-divider></v-divider>
    <v-list density="comfortable" nav>
      <template v-for="(item, index) in navItems" :key="index">
        <template v-if="item.type === 'subheader'">
          <v-list-subheader v-if="!item.isHidden || isSuperAdmin" class="text-uppercase mt-3">
            {{ item.title }}
          </v-list-subheader>
        </template>

        <template v-else>
          <v-list-item v-if="!item.isHidden || isSuperAdmin" :prepend-icon="item.prependIcon" :title="item.title"
            :value="item.value" color="primary" :to="item.to" @click="item.action ? handleLogout() : null">
            <template v-if="item.value === 'attendance-resolutions' && pendingResolutions > 0" v-slot:append>
              <v-chip size="small" color="error" variant="flat">{{ pendingResolutions }}</v-chip>
            </template>
            <template v-else-if="item.value === 'employee-creations' && pendingCreations > 0" v-slot:append>
              <v-chip size="small" color="error" variant="flat">{{ pendingCreations }}</v-chip>
            </template>
            <template v-else-if="item.value === 'salary-change-requests' && pendingSalaryChangeRequests > 0"
              v-slot:append>
              <v-chip size="small" color="error" variant="flat">{{ pendingSalaryChangeRequests }}</v-chip>
            </template>
          </v-list-item>
        </template>
      </template>
    </v-list>
  </v-navigation-drawer>

  <v-app-bar :elevation="2">
    <template v-slot:prepend>
      <v-app-bar-nav-icon :class="{ 'icon-animated': menuIconToggled }" @click="toggleDrawer"></v-app-bar-nav-icon>
    </template>

    <v-spacer></v-spacer>

    <!-- Clock & Date -->
    <div class="text-right mr-4">
      <div>{{ time }}</div>
      <div class="text-caption">{{ date }}</div>
    </div>

    <v-tooltip text="Switch Portal" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn v-bind="props" icon :to="{ name: 'jobs' }">
          <v-icon>mdi-swap-horizontal</v-icon>
        </v-btn>
      </template>
    </v-tooltip>
  </v-app-bar>

  <LogoutDialog ref="logout" />
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import LogoutDialog from "@/components/LogoutDialog";
import { hasRole, hasPermission, hasCompanyPermission } from "@/helpers/auth";
import axios from "axios";

export default {
  components: { LogoutDialog },

  data: () => ({
    drawer: null,
    menuIconToggled: false,
    time: "",
    date: "",
    clockInterval: null,
    pendingResolutions: 0,
    pendingCreations: 0,
    pendingSalaryChangeRequests: 0,
  }),

  async mounted() {
    this.updateDateTime();
    this.clockInterval = setInterval(this.updateDateTime, 1000);

    // Approvers (Audit / Accounting) see how many no time in/out requests wait for them.
    if (hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, "Approve Attendance Resolution")) {
      try {
        const { data } = await axios.get("admin/attendance-resolutions", {
          params: { status: "Pending", itemsPerPage: 1 },
        });

        this.pendingResolutions = data?.total ?? 0;
      } catch (error) {
        this.pendingResolutions = 0;
      }
    }

    // The PM / CH, the approver a company named and the Audit / Accounting
    // validators see how many employee creation requests wait for them.
    if (hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, [
          "Approve Employee Creation (PM)",
          "Approve Employee Creation (CH)",
          "Approve Employee Creation (Approver 1)",
          "Validate Employee Creation (Audit)",
          "Validate Employee Creation (Accounting)",
        ])) {
      try {
        const { data } = await axios.get("admin/employee-creations", {
          params: { itemsPerPage: 1 },
        });

        this.pendingCreations = data?.total ?? 0;
      } catch (error) {
        this.pendingCreations = 0;
      }
    }

    // Audit / Accounting see how many salary detail changes wait for them: the API's
    // awaiting_me=1 narrows the list to the step the account takes (Accounting first, then
    // Audit), so each of them reads its own queue. Like the page itself, the badge is only
    // read for an account that works on a company which runs the approval chain.
    if (
      hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, [
        "Approve Salary Detail Change (Accounting)",
        "Approve Salary Detail Change (Audit)",
      ])
    ) {
      try {
        const { data } = await axios.get("admin/salary-change-requests", {
          params: { awaiting_me: 1, itemsPerPage: 1 },
        });

        this.pendingSalaryChangeRequests = data?.total ?? 0;
      } catch (error) {
        this.pendingSalaryChangeRequests = 0;
      }
    }
  },

  beforeUnmount() {
    clearInterval(this.clockInterval);
  },

  computed: {
    ...mapGetters("auth", ["user", "userRoles", "userPermissions", "companySettings", "anyCompanyHasSetting"]),

    isSuperAdmin() {
      return hasRole(this.userRoles, "");
    },

    navItems() {
      return [
        {
          prependIcon: "mdi-view-dashboard-variant",
          title: "Dashboard",
          value: "dashboard",
          to: { name: "dashboard" },
          isHidden: !hasPermission(this.userPermissions, "Access Dashboard"),
        },
        {
          prependIcon: "mdi-office-building-outline",
          title: "Companies",
          value: "companies",
          to: { name: "companies" },
          isHidden: !hasPermission(this.userPermissions, "Access Companies"),
        },
        {
          prependIcon: "mdi-clock-outline",
          title: "Company Schedules",
          value: "companies-schedules",
          to: { name: "companies-schedules" },
          isHidden: !hasPermission(
            this.userPermissions,
            "Access Company Schedules"
          ),
        },
        {
          prependIcon: "mdi-calendar-clock",
          title: "Shifts",
          value: "shifts",
          to: { name: "shifts" },
          isHidden: !hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, "Access Shifts"),
        },
        {
          prependIcon: "mdi-group",
          title: "Departments",
          value: "departments",
          to: { name: "departments" },
          isHidden: !hasPermission(this.userPermissions, "Access Departments"),
        },
        {
          prependIcon: "mdi-account-group-outline",
          title: "Groups",
          value: "groups",
          to: { name: "groups" },
          isHidden: !hasPermission(this.userPermissions, "Access Groups"),
        },
        {
          prependIcon: "mdi-account-supervisor-circle-outline",
          title: "Department Roles",
          value: "department-roles",
          to: { name: "department-roles" },
          isHidden: !hasPermission(
            this.userPermissions,
            "Access Department Roles"
          ),
        },
        {
          prependIcon: "mdi-clock",
          title: "Attendances",
          value: "attendances",
          to: { name: "attendances" },
          isHidden: !hasPermission(this.userPermissions, "Access Attendances"),
        },
        {
          prependIcon: "mdi-clock-check-outline",
          title: "Overtime Approvals",
          value: "overtime-approvals",
          to: { name: "overtime-approvals" },
          isHidden: !(hasPermission(this.userPermissions, "Access Overtime Approvals") || hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, [
            "Validate Overtime",
            "Approve Overtime (PE)",
            "Approve Overtime (PM)",
            "Approve Overtime (CH)",
          ])),
        },
        {
          prependIcon: "mdi-clipboard-check-outline",
          title: "Attendance Verifications",
          value: "attendance-verifications",
          to: { name: "attendance-verifications" },
          isHidden: !hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, "Access Attendance Verifications"),
        },
        {
          prependIcon: "mdi-file-document-alert-outline",
          title: "Attendance Resolutions",
          value: "attendance-resolutions",
          to: { name: "attendance-resolutions" },
          // There is no separate "access" permission: the page is used by the requesters
          // (Timekeeper) to file and follow up the requests and by the approvers (Audit /
          // Accounting) to accept them.
          isHidden: !(hasPermission(this.userPermissions, "Access Attendance Resolutions") || hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, [
            "Request Attendance Resolution",
            "Approve Attendance Resolution",
          ])),
        },
        {
          prependIcon: "mdi-history",
          title: "Activity Logs",
          value: "activity-logs",
          to: { name: "activity-logs" },
          isHidden: !hasPermission(this.userPermissions, "Access Activity Logs"),
        },
        {
          prependIcon: "mdi-calendar-clock",
          title: "Attendance Requests",
          value: "attendance-requests",
          to: { name: "attendance-requests" },
          isHidden: !hasPermission(
            this.userPermissions,
            "Access Attendance Requests"
          ),
        },
        {
          prependIcon: "mdi-calendar-arrow-right",
          title: "Schedule",
          value: "schedules",
          to: { name: "schedules" },
          isHidden: !hasPermission(
            this.userPermissions,
            "Access Employee Schedules"
          ),
        },
        {
          prependIcon: "mdi-umbrella-beach-outline",
          title: "Holidays",
          value: "holidays",
          to: { name: "holidays" },
          isHidden: !hasPermission(this.userPermissions, "Access Holidays"),
        },
        {
          prependIcon: "mdi-credit-card",
          title: "Payrolls",
          value: "payrolls",
          to: { name: "payrolls" },
          isHidden: !hasPermission(this.userPermissions, "Access Payrolls"),
        },
        {
          prependIcon: "mdi-cash-refund",
          title: "Claim Salary",
          value: "claim-salary",
          to: { name: "claim-salary" },
          isHidden: !hasPermission(this.userPermissions, "Access Claim Salary"),
        },
        {
          prependIcon: "mdi-view-dashboard-variant",
          title: "Salary Increase",
          value: "salary_increase",
          to: { name: "salary_increase" },
          isHidden: !hasPermission(
            this.userPermissions,
            "Access Salary Increase"
          ),
        },
        {
          prependIcon: "mdi-cash-edit",
          title: "Salary Change Requests",
          value: "salary-change-requests",
          to: { name: "salary-change-requests" },
          // There is no separate "access" permission: the requesters file the change and
          // follow it up, Accounting approves it first and Audit gives the final approval -
          // which is the one that writes the salary details. The page is part of the
          // workforce approval chain as well, so it only exists for an account that works on
          // a company which runs one of the workflow switches (helpers/auth.js asks the same
          // question for the route, so a bookmarked address cannot slip past the menu).
          isHidden: !(hasPermission(this.userPermissions, "Access Salary Detail Change Requests") || hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, [
            "Request Salary Detail Change",
            "Approve Salary Detail Change (Accounting)",
            "Approve Salary Detail Change (Audit)",
          ])),
        },
        {
          prependIcon: "mdi-cash",
          title: "Loans",
          value: "loans",
          to: { name: "loans" },
          isHidden: !hasPermission(this.userPermissions, "Access Loans"),
        },
        {
          prependIcon: "mdi-cash-multiple",
          title: "Loan Types",
          value: "loan-types",
          to: { name: "loan-types" },
          isHidden: !hasPermission(this.userPermissions, "Access Loan Types"),
        },
        {
          type: "subheader",
          title: "Users",
          isHidden: !hasPermission(this.userPermissions, [
            "Access Admins",
            "Access Employees",
            "Access Applicants",
            "Create Accounts",
          ]),
        },
        {
          prependIcon: "mdi-shield-account",
          title: "Assign Users",
          value: "admins",
          to: { name: "admins" },
          isHidden: !hasPermission(this.userPermissions, [
            "Access Admins",
            "Create Accounts",
          ]),
        },
        {
          prependIcon: "mdi-account-tie",
          title: "Employees",
          value: "employees",
          to: { name: "employees" },
          isHidden: !hasPermission(this.userPermissions, "Access Employees"),
        },
        {
          prependIcon: "mdi-account-plus-outline",
          title: "Employee Creations",
          value: "employee-creations",
          to: { name: "employee-creations" },
          // No separate "access" permission: the TimeKeeper files here, the Project
          // Manager / Construction Head approve, the approver a company names
          // approves, and Audit / Accounting validate, so any of those permissions
          // grants the page.
          isHidden: !hasCompanyPermission(this.userPermissions, this.userRoles, this.companySettings, [
            "Request Employee Creation",
            "Approve Employee Creation (PM)",
            "Approve Employee Creation (CH)",
            "Approve Employee Creation (Approver 1)",
            "Validate Employee Creation (Audit)",
            "Validate Employee Creation (Accounting)",
          ]),
        },
        {
          prependIcon: "mdi-account",
          title: "Applicants",
          value: "applicants",
          to: { name: "applicants" },
          isHidden: !hasPermission(this.userPermissions, "Access Applicants"),
        },
        {
          type: "subheader",
          title: "Careers",
          isHidden: !hasPermission(this.userPermissions, [
            "Access Jobs",
            "Access Applications",
            "Access Qualifications",
            "Access Responsibilities",
          ]),
        },
        {
          prependIcon: "mdi-briefcase",
          title: "Jobs",
          value: "jobs",
          to: { name: "adminJobs" },
          isHidden: !hasPermission(this.userPermissions, "Access Jobs"),
        },
        {
          prependIcon: "mdi-briefcase-account",
          title: "Applications",
          value: "applications",
          to: { name: "applications" },
          isHidden: !hasPermission(this.userPermissions, "Access Applications"),
        },
        {
          prependIcon: "mdi-briefcase-check",
          title: "Qualifications",
          value: "qualifications",
          to: { name: "qualifications" },
          isHidden: !hasPermission(
            this.userPermissions,
            "Access Qualifications"
          ),
        },
        {
          prependIcon: "mdi-briefcase-clock",
          title: "Responsibilities",
          value: "responsibilities",
          to: { name: "responsibilities" },
          isHidden: !hasPermission(
            this.userPermissions,
            "Access Responsibilities"
          ),
        },
        {
          type: "subheader",
          title: "Settings",
        },
        {
          prependIcon: "mdi-account-key-outline",
          title: "Roles",
          value: "roles",
          to: { name: "roles" },
          isHidden: !hasPermission(this.userPermissions, "Access Roles"),
        },
        {
          prependIcon: "mdi-shield-edit",
          title: "Permissions",
          value: "permissions",
          to: { name: "permissions" },
          isHidden: !hasPermission(this.userPermissions, "Access Permissions"),
        },
        {
          prependIcon: "mdi-account-cog",
          title: "Account",
          value: "account",
          to: { name: "account" },
        },
        {
          prependIcon: "mdi-logout",
          title: "Logout",
          value: "logout",
          action: "logout",
        },
      ];
    },
  },

  methods: {
    ...mapActions("auth", ["logout"]),

    async handleLogout() {
      this.$refs.logout.show();
      await this.logout();
      this.$router.push({ name: "login" });
      this.$refs.logout.hide();
    },

    toggleDrawer() {
      this.drawer = !this.drawer;
      this.menuIconToggled = true;
      setTimeout(() => {
        this.menuIconToggled = false;
      }, 300);
    },
    updateDateTime() {
      const now = new Date();
      this.time = now.toLocaleTimeString();
      this.date = now.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.icon-animated {
  animation: rotate-icon 0.3s ease;
}

@keyframes rotate-icon {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(180deg);
  }
}

.text-right {
  text-align: right;
}
</style>
